import { formatTime } from "@vromo/models/PolicyRule";

export class Unit {
    label: string | null;
    multiplier: number;
    type: string | null;

    constructor(label: string | null, multiplier: number, type: string | null) {
        this.label = label;
        this.multiplier = multiplier;
        this.type = type;
    }
};

export const unitProperties = [
    new Unit('Km', 1, 'distance'),
    new Unit('Seconds', 1000, 'time'),
    new Unit('Minutes', 60000, 'time'),
    new Unit('Hours', 3600000, 'time'),
    new Unit('Jobs', 1, 'quantity'),
];

function calculateTimeUnit(ms: number): Unit {
    const timeProperties = unitProperties.filter((tp) => tp.type === 'time');
    if (ms % 1000 === 0) {
        return timeProperties.reverse().find((tp) => ms % tp.multiplier! === 0)!;
    } else {
        return timeProperties[0]!;
    }
}

type ConditionItemDefinition = PillConfiguration & {
    decimals?: number;
    default: number | null | boolean;
    label: string;
    max?: number;
    options?: { value: string | number, label: string }[];
    simulationMessages: { success: string; failed: string; }
    tag: (value: string | number | boolean) => string;
    transformOnBlur?: (value: string) => number | string;
    type: ConditionType;
    unitGenerator?: (value: number) => Unit;
};

type PillConfiguration = {
    color: string;
    description: string;
    icon: string;
}

export const pillConfigurations = new Map<string, PillConfiguration>([
    ['mode', {
        color: 'hsl(120, 76%, 69%)',
        description: 'Rule Mode',
        icon: 'share-all',
    }],
    ['team', {
        color: 'hsl(40, 76%, 69%)',
        description: 'Team',
        icon: 'users',
    }]
]);

const getFloat = (value: string, decimals: number) => parseFloat(parseFloat(value).toFixed(decimals));

export const getDistance = (value: string): number => getFloat(value, 1);

export const teamSimulationSimulationMessages = {
    success: 'Driver belongs to team',
    failed: 'Driver does not belong to team'
};

export const conditionDefinitions = new Map<string, ConditionItemDefinition>([
    ['range', {
        color: 'hsl(150, 76%, 69%)',
        decimals: 2,
        default: null,
        description: 'Distance to job',
        icon: 'route',
        label: 'Distance to job is less than',
        max: 21000, // 20,036km
        simulationMessages: {
            success: 'Driver in range of job',
            failed: 'Driver not in range of job',
        },
        tag: (value) => `< ${value}`,
        type: 'distance',
        unitGenerator: () => new Unit('Km', 1, 'distance'),
    }],
    ['afterCreation', {
        color: 'hsl(195, 76%, 69%)',
        default: null,
        description: 'Time after creation',
        icon: 'hourglass-start',
        label: 'Time passed after job creation is at least',
        max: 31557600000, // 1 year
        simulationMessages: {
            success: 'Outside of job creation time',
            failed: 'Not enough time has passed since job creation'
        },
        tag: (value) => `> ${formatTime(value as number)}`,
        transformOnBlur: parseInt,
        type: 'time',
        unitGenerator: calculateTimeUnit,
    }],
    ['beforeDeadline', {
        color: 'hsl(360, 76%, 75%)',
        default: null,
        description: 'Time before next deadline',
        icon: 'hourglass-end',
        label: 'Time before next deadline is less than',
        max: 31557600000, // 1 year
        simulationMessages: {
            success: 'Within time period of next deadline',
            failed: 'Not within time to next deadline'
        },
        tag: (value) => `< ${formatTime(value as number)}`,
        transformOnBlur: parseInt,
        type: 'time',
        unitGenerator: calculateTimeUnit,
    }],
    ['maxJobs', {
        color: 'hsl(210, 76%, 75%)',
        default: null,
        description: 'Max number of jobs assigned to driver',
        icon: 'clipboard-list',
        label: 'Maximum number of jobs the driver has is',
        max: 1000,
        simulationMessages: {
            success: 'Driver has room for more jobs',
            failed: 'Driver has too many jobs'
        },
        tag: (value) => `< ${value} Jobs`,
        transformOnBlur: parseInt,
        type: 'quantity',
        unitGenerator: () => new Unit('Jobs', 1, 'quantity'),
    }],
    ['pickupToPickup', {
        color: 'hsl(150, 76%, 69%)',
        decimals: 2,
        default: null,
        description: 'Distance from pickup to next pickup',
        icon: 'route',
        label: 'Distance from current pickup address to next pickup address is less than',
        max: 21000,
        simulationMessages: {
            success: 'Distance from pickup to next pickup is within range',
            failed: 'Distance from pickup to next pickup is not within range'
        },
        tag: (value) => `< ${value}`,
        type: 'distance',
        unitGenerator: () => new Unit('Km', 1, 'distance'),
    }],
    ['dropoffToDropoff', {
        color: 'hsl(150, 76%, 69%)',
        decimals: 2,
        default: null,
        description: 'Distance from dropoff to next dropoff',
        icon: 'route',
        label: 'Distance from current dropoff address to next dropoff address is less than',
        max: 21000,
        simulationMessages: {
            success: 'Distance from dropoff to next dropoff is within range',
            failed: 'Distance from dropoff to next dropoff is not within range'
        },
        tag: (value) => `< ${value}`,
        type: 'distance',
        unitGenerator: () => new Unit('Km', 1, 'distance'),
    }],
    ['dropoffToPickup', {
        color: 'hsl(150, 76%, 69%)',
        decimals: 2,
        default: null,
        description: 'Distance from dropoff to next pickup',
        icon: 'route',
        label: 'Distance from current dropoff address to next pickup address is less than',
        max: 21000,
        simulationMessages: {
            success: 'Distance from dropoff to next pickup is within range',
            failed: 'Distance from dropoff to next pickup is not within range'
        },
        tag: (value) => `< ${value}`,
        type: 'distance',
        unitGenerator: () => new Unit('Km', 1, 'distance'),
    }],
    ['pickupToDropoffGreaterThan', {
        color: '#c1b2ee',
        decimals: 2,
        default: null,
        description: 'Distance from pickup to dropoff',
        icon: 'ruler',
        label: 'Distance from pickup to dropoff is greater than',
        max: 21000,
        simulationMessages: {
            success: 'Distance from pickup to dropoff is within range',
            failed: 'Distance from pickup to dropoff is not within range'
        },
        tag: (value) => `> ${value}`,
        type: 'distance',
        unitGenerator: () => new Unit('Km', 1, 'distance'),
    }],
    ['pickupToDropoffLessThan', {
        color: '#c1b2ee',
        decimals: 2,
        default: null,
        description: 'Distance from pickup to dropoff',
        icon: 'ruler',
        label: 'Distance from pickup to dropoff is less than',
        simulationMessages: {
            success: 'Distance from pickup to dropoff is within range',
            failed: 'Distance from pickup to dropoff is not within range'
        },
        max: 21000,
        tag: (value) => `< ${value}`,
        type: 'distance',
        unitGenerator: () => new Unit('Km', 1, 'distance'),
    }],
    ['checkControlledContents', {
        color: '#ddabea',
        default: false,
        description: 'Job contains restricted items (alcohol, tobacco, other)',
        icon: 'wine-bottle',
        label: 'Job contains restricted items (alcohol, tobacco, other)',
        simulationMessages: {
            success: 'Job contains restricted items',
            failed: 'Job does not contain restricted items'
        },
        tag: () => 'There are restricted items',
        type: 'boolean',
    }],
    ['orderValueLessThan', {
        color: '#fdffc4',
        decimals: 2,
        default: null,
        description: 'Order value',
        icon: 'money-bill',
        label: 'Order value is less than',
        simulationMessages: {
            success: 'Order value is less than required amount',
            failed: 'Order value is not less than required amount'
        },
        tag: (value) => `< ${value as number / 100}`,
        transformOnBlur: function (value) { return getFloat(value, this.decimals!) },
        type: 'currency',
        unitGenerator: () => new Unit(null, 100, 'currency'),
    }],
    ['orderValueGreaterThan', {
        color: '#fdffc4',
        decimals: 2,
        default: null,
        description: 'Order value',
        icon: 'money-bill',
        label: 'Order value is greater than',
        simulationMessages: {
            success: 'Order value is greater than required amount',
            failed: 'Order value is not greater than required amount'
        },
        tag: (value) => `> ${value as number / 100}`,
        transformOnBlur: function (value) { return getFloat(value, this.decimals!) },
        type: 'currency',
        unitGenerator: () => new Unit(null, 100, 'currency'),
    }],
    ['paymentMethod', {
        color: '#666',
        default: null,
        description: 'Payment method',
        icon: 'credit-card',
        label: 'Payment method is',
        options: [
            { label: 'Cash', value: 'cash' },
            { label: 'Credit card', value: 'credit' },
            // { label: 'Other', value: 'other' },
        ],
        simulationMessages: {
            success: 'Payment method is the required type',
            failed: 'Payment method is not the required type'
        },
        tag: function (optionValue) { return this.options?.find(({ value }) => value === optionValue)?.label.toString() || optionValue.toString() },
        type: 'enum',
    }],
]);

export type ConditionType = 'boolean' | 'string' | 'enum' | 'distance' | 'quantity' | 'time' | 'currency';

export class Condition {
    property?: string | null = null;
    type?: ConditionType | null = null;
    unit?: Unit | null = null;
    value?: string | number | boolean | null = null;

    constructor(props?: Condition) {
        const {
            property = null, value = null, unit = null
        } = props || {}
        this.unit = unit;
        this.value = value;
        this.property = property;
        if (property) this.type = conditionDefinitions.get(property)!.type;
        if (!this.unit) this.unit = conditionDefinitions.get(property as string)?.unitGenerator?.(value as number);
        if (this.unit?.multiplier) this.value = value as number / this.unit!.multiplier;
    }
}
